'use client';

import { useEffect, useMemo, type ReactNode } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { NavigationContext } from '@/contexts/NavigationContext';
import type {
  NavigationState,
  ScreenName,
  ScreenParams,
  TabStack,
} from '@/types/navigation';
import {
  buildScreenUrl,
  isKnownScreenPath,
  pathnameToScreen,
  pathnameToTab,
  TAB_URL_MAP,
} from '@/lib/screen-url-map';
import { stripLangPrefix } from '@/i18n/locale';
import { useLocalizedHref } from '@/hooks/useLocalizedHref';


type Tab = keyof TabStack;

const PARENT_QUERY_STORAGE_KEY = 'shot:nav-parent-query';

const PARENT_QUERY_LRU_CAP = 32;

function rememberParentQueries(pathname: string, search: string): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = sessionStorage.getItem(PARENT_QUERY_STORAGE_KEY);
    const map: Record<string, string> = raw ? JSON.parse(raw) : {};
    if (search) {
      delete map[pathname];
      map[pathname] = search;
    } else {
      delete map[pathname];
    }
    const keys = Object.keys(map);
    if (keys.length > PARENT_QUERY_LRU_CAP) {
      for (const k of keys.slice(0, keys.length - PARENT_QUERY_LRU_CAP)) {
        delete map[k];
      }
    }
    sessionStorage.setItem(PARENT_QUERY_STORAGE_KEY, JSON.stringify(map));
  } catch {
  }
}

function popRememberedQuery(pathname: string): string {
  if (typeof window === 'undefined') return '';
  try {
    const raw = sessionStorage.getItem(PARENT_QUERY_STORAGE_KEY);
    if (!raw) return '';
    const map: Record<string, string> = JSON.parse(raw);
    const search = map[pathname] ?? '';
    if (search) {
      delete map[pathname];
      sessionStorage.setItem(PARENT_QUERY_STORAGE_KEY, JSON.stringify(map));
    }
    return search;
  } catch {
    return '';
  }
}

function goBackNative(router: ReturnType<typeof useRouter>): boolean {
  if (typeof window === 'undefined') return false;
  if (window.history.length <= 1) return false;
  router.back();
  return true;
}

function goBackByHierarchy(
  router: ReturnType<typeof useRouter>,
  pathname: string,
  activeTab: Tab,
  localize: (href: string) => string,
): void {
  const targetWithQuery = (parent: string): string => {
    const search = popRememberedQuery(parent);
    return localize(search ? `${parent}?${search}` : parent);
  };

  const tabRoot = TAB_URL_MAP[activeTab];
  if (pathname === tabRoot) {
    router.push(localize('/home'));
    return;
  }
  const segments = pathname.split('/').filter(Boolean);
  while (segments.length > 0) {
    segments.pop();
    const parent = segments.length === 0 ? '/' : '/' + segments.join('/');
    if (parent === tabRoot || isKnownScreenPath(parent)) {
      router.push(targetWithQuery(parent));
      return;
    }
  }
  router.push(targetWithQuery(tabRoot));
}

const EMPTY_STACKS: TabStack = {
  balance: [{ screen: 'balance' }],
  cases: [{ screen: 'cases' }],
  games: [{ screen: 'games' }],
  inventory: [{ screen: 'inventory' }],
  referrals: [{ screen: 'referrals' }],
  profile: [{ screen: 'profile' }],
  onboarding: [{ screen: 'onboarding' }],
  shop: [{ screen: 'shop' }],
};

// WHY: useSearchParams здесь переведёт все маршруты в dynamic rendering и сломает ISR на маркетинговой `/`
export function WebUrlNavigationProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = stripLangPrefix(usePathname());
  const searchParams = useSearchParams();
  const localize = useLocalizedHref();

  useEffect(() => {
    rememberParentQueries(pathname, searchParams.toString());
  }, [pathname, searchParams]);

  const value = useMemo(() => {
    // WHY: стеку и «назад» вкладка нужна всегда; у вики её нет, и подсветку там решает useHighlightedTab
    const activeTab: Tab = pathnameToTab(pathname) ?? 'balance';
    const currentScreen: ScreenName = pathnameToScreen(pathname) ?? activeTab;

    const stacks: TabStack = {
      ...EMPTY_STACKS,
      [activeTab]: [{ screen: currentScreen, params: {} }],
    };

    const state: NavigationState = { activeTab, stacks };

    return {
      state,
      changeTab: (tab: Tab) => {
        router.push(localize(TAB_URL_MAP[tab]));
      },
      pushScreen: (_tab: Tab, screen: ScreenName, p?: ScreenParams) => {
        router.push(localize(buildScreenUrl(screen, p)));
      },
      replaceScreen: (_tab: Tab, screen: ScreenName, p?: ScreenParams) => {
        router.replace(localize(buildScreenUrl(screen, p)));
      },
      popScreen: (_tab: Tab) => {
        if (goBackNative(router)) return;
        goBackByHierarchy(router, pathname, activeTab, localize);
      },
      resetStack: (_tab: Tab, screen: ScreenName, p?: ScreenParams) => {
        router.replace(localize(buildScreenUrl(screen, p)));
      },
      goBack: () => {
        if (goBackNative(router)) return;
        goBackByHierarchy(router, pathname, activeTab, localize);
      },
      navigate: (screen: ScreenName, p?: ScreenParams) => {
        router.push(localize(buildScreenUrl(screen, p)));
      },
      getCurrentScreen: () => ({ screen: currentScreen, params: {} }),
      canGoBack: () => {
        if (pathname === '/home' || pathname === '/') return false;
        return typeof window !== 'undefined' && window.history.length > 1;
      },
      updateCurrentParams: (next: ScreenParams) => {
        if (typeof window === 'undefined') return;
        const url = new URL(window.location.href);
        for (const [k, v] of Object.entries(next)) {
          if (v === undefined || v === null || v === '') {
            url.searchParams.delete(k);
          } else {
            url.searchParams.set(k, String(v));
          }
        }
        router.replace(url.pathname + url.search + url.hash);
      },
    };
  }, [pathname, router, localize]);

  return (
    <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
  );
}
